import "leaflet/dist/leaflet.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { ChevronDown, ChevronRight, MapPin } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { SiteHeader, SiteFooter } from "@/components/Layout";
import CategorySheet from "@/components/CategorySheet";
import { api, imgSrc } from "@/lib/api";
import { useI18n } from "@/i18n";

// Colour each pin by its primary tag/category so the map reads at a glance.
const TAG_COLORS = {
  food: "#e4572e",
  grocery: "#f0a202",
  beaches: "#1b9aaa",
  retreats: "#2a9d8f",
  clubs: "#7b2cbf",
  shop: "#e4572e",
  centers: "#2a9d8f",
  activities: "#1b9aaa",
};

function colorFor(loc) {
  const keys = [...(loc.tags || []), ...(loc.categories || [])];
  for (const k of keys) if (TAG_COLORS[k]) return TAG_COLORS[k];
  return "#0a0a0a";
}

function pinIcon(color) {
  const html = `<svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="${color}"/>
    <circle cx="15" cy="15" r="5.5" fill="#fff"/>
  </svg>`;
  return L.divIcon({
    className: "sj-pin",
    html,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -38],
  });
}

// Keeps the Leaflet view in sync with the filtered locations + the focused item.
function MapController({ locations, focusId }) {
  const map = useMap();

  useEffect(() => {
    if (!locations.length) return;
    const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 16 });
  }, [locations, map]);

  useEffect(() => {
    if (!focusId) return;
    const l = locations.find((x) => x.id === focusId);
    if (l) map.flyTo([l.lat, l.lng], 17, { duration: 0.8 });
  }, [focusId, locations, map]);

  return null;
}

export default function MapPage() {
  const { t, lang, tagLabel } = useI18n();
  const [center, setCenter] = useState({ lat: 10.0345, lng: -85.7379 });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const markerRefs = useRef({});

  useEffect(() => {
    document.title = "Map — San Juanillo Rising";
    setLoading(true);
    api
      .get(`/map/locations?lang=${lang}`)
      .then((res) => {
        if (res.data.center) setCenter(res.data.center);
        setLocations(res.data.locations || []);
      })
      .catch(() => setLocations([]))
      .finally(() => setLoading(false));
  }, [lang]);

  const tagCounts = useMemo(() => {
    return locations.reduce((acc, l) => {
      (l.tags || []).forEach((t) => { acc[t] = (acc[t] || 0) + 1; });
      return acc;
    }, {});
  }, [locations]);
  const allTags = Object.keys(tagCounts).sort((a, b) => a.localeCompare(b));
  const shown = activeTag ? locations.filter((l) => (l.tags || []).includes(activeTag)) : locations;

  const focusMarker = (id) => {
    setFocusId(id);
    const m = markerRefs.current[id];
    if (m) setTimeout(() => m.openPopup(), 400);
  };

  return (
    <div className="dd-root" data-testid="map-page">
      <SiteHeader />
      <main className="dd-feed feed-top">
        <div className="dd-wrap">
          <header className="map-head">
            <h1 className="map-title" data-testid="map-title">{t("mapTitle")}</h1>
            <p className="map-sub">{t("mapSub")}</p>
          </header>

          {!loading && allTags.length > 0 ? (
            <div className="dd-filter-row map-filter-row">
              <button className="dd-cat-btn" onClick={() => setSheetOpen(true)} data-testid="map-category-filter-btn">
                {activeTag ? tagLabel(activeTag) : t("allCategories")} <ChevronDown size={16} strokeWidth={2.5} />
              </button>
              <span className="dd-listings-count" data-testid="map-listings-count">{shown.length} {t("listings")}</span>
            </div>
          ) : null}

          {loading ? (
            <p style={{ padding: "40px 0", color: "var(--muted)" }}>{t("loading")}</p>
          ) : locations.length === 0 ? (
            <p style={{ padding: "40px 0", color: "var(--muted)" }} data-testid="map-empty">
              {t("mapEmpty")}
            </p>
          ) : (
            <div className="map-layout">
              <div className="map-canvas" data-testid="map-canvas">
                <MapContainer
                  center={[center.lat, center.lng]}
                  zoom={15}
                  scrollWheelZoom={false}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    maxZoom={19}
                  />
                  <MapController locations={shown} focusId={focusId} />
                  {shown.map((l) => (
                    <Marker
                      key={l.id}
                      position={[l.lat, l.lng]}
                      icon={pinIcon(colorFor(l))}
                      ref={(ref) => { if (ref) markerRefs.current[l.id] = ref; }}
                    >
                      <Popup>
                        <div className="map-popup" data-testid={`map-popup-${l.slug}`}>
                          {l.featured_image ? (
                            <img className="map-popup-img" src={imgSrc(l.featured_image)} alt={l.title} />
                          ) : null}
                          <h3 className="map-popup-title">{l.title}</h3>
                          {l.tags && l.tags.length ? (
                            <div className="map-popup-tags">
                              {l.tags.map((tag) => (
                                <span className="dd-tag-chip" key={tag}>{tagLabel(tag)}</span>
                              ))}
                            </div>
                          ) : null}
                          {l.excerpt ? <p className="map-popup-excerpt">{l.excerpt}</p> : null}
                          <div className="map-popup-actions">
                            {l.phone ? (
                              <a
                                href={`https://wa.me/${l.phone.replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noreferrer"
                                className="dd-contact-link"
                                data-testid={`map-phone-${l.slug}`}
                              >
                                <WhatsAppIcon size={14} /> {l.phone}
                              </a>
                            ) : null}
                            <a
                              href={l.location_url}
                              target="_blank"
                              rel="noreferrer"
                              className="dd-contact-link"
                              data-testid={`map-directions-${l.slug}`}
                            >
                              <MapPin size={14} strokeWidth={2} /> {t("directions")}
                            </a>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>

              <aside className="map-list" data-testid="map-list">
                {shown.map((l) => (
                  <button
                    key={l.id}
                    className={`map-list-item${focusId === l.id ? " is-active" : ""}`}
                    onClick={() => focusMarker(l.id)}
                    data-testid={`map-list-item-${l.slug}`}
                  >
                    <span className="map-list-thumb">
                      {l.featured_image ? <img src={imgSrc(l.featured_image)} alt="" /> : null}
                      <span className="map-list-dot" style={{ background: colorFor(l) }} />
                    </span>
                    <span className="map-list-body">
                      <span className="map-list-title">{l.title}</span>
                      {l.tags && l.tags.length ? (
                        <span className="map-list-tag">{tagLabel(l.tags[0])}</span>
                      ) : null}
                    </span>
                    <ChevronRight className="map-list-chev" size={16} strokeWidth={2} aria-hidden="true" />
                  </button>
                ))}
              </aside>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
      <CategorySheet
        open={sheetOpen}
        tags={allTags.map((tag) => ({ tag, count: tagCounts[tag] }))}
        total={locations.length}
        activeTag={activeTag}
        onPick={(tag) => { setActiveTag(tag); setSheetOpen(false); }}
        onClose={() => setSheetOpen(false)}
      />
    </div>
  );
}
